import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Chip
} from '@mui/material';
import {
  Share as ShareIcon,
  Business as BusinessIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { FirebaseService } from '../../../services/FirebaseService';
import { UserProfile } from '../../../types/models';

interface ShareReportDialogProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle: string;
  onShared?: () => void;
}

export const ShareReportDialog: React.FC<ShareReportDialogProps> = ({
  open,
  onClose,
  reportId,
  reportTitle,
  onShared
}) => {
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAlreadyShared, setIsAlreadyShared] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open) {
      loadUserProfile();
      checkIfAlreadyShared();
    }
  }, [open, reportId]); // checkIfAlreadyShared is stable within this effect scope

  const loadUserProfile = async () => {
    try {
      const profile = await FirebaseService.fetchUserProfile();
      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Failed to load user profile');
    }
  };

  const checkIfAlreadyShared = async () => {
    try {
      const isShared = await FirebaseService.isReportShared(reportId);
      setIsAlreadyShared(isShared);
    } catch (error) {
      console.error('Error checking if report is shared:', error);
    }
  };

  const handleShare = async () => {
    if (!userProfile?.companyId) {
      setError('You must be part of a company to share reports');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await FirebaseService.shareReportToCompany(reportId);
      setSuccess('Report shared with your company successfully!');
      setIsAlreadyShared(true);
      
      if (onShared) {
        onShared();
      }
      
      // Close dialog after short delay
      setTimeout(() => {
        onClose();
        setSuccess('');
      }, 2000);
    } catch (error: any) {
      console.error('Error sharing report:', error);
      setError(error.message || 'Failed to share report');
    } finally {
      setLoading(false);
    }
  };

  const handleUnshare = async () => {
    try {
      setLoading(true);
      setError('');
      
      await FirebaseService.unshareReport(reportId);
      setSuccess('Report unshared successfully');
      setIsAlreadyShared(false);
      
      if (onShared) {
        onShared();
      }
      
      // Close dialog after short delay
      setTimeout(() => {
        onClose();
        setSuccess('');
      }, 2000);
    } catch (error: any) {
      console.error('Error unsharing report:', error);
      setError(error.message || 'Failed to unshare report');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    onClose();
  };

  // No company case
  if (!userProfile?.companyId) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ShareIcon />
            Share Report
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={3}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Join a Company First
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You need to be part of a company to share reports. Go to the Shared tab to create or join a company.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ShareIcon />
          Share Report
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={2}>
          <Typography variant="h6" gutterBottom>
            "{reportTitle}"
          </Typography>
          
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="body2" color="text.secondary">
              Share with:
            </Typography>
            <Chip 
              icon={<BusinessIcon />}
              label={userProfile.companyName}
              color="primary"
              size="small"
            />
          </Box>

          {isAlreadyShared ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckIcon />
                This report is already shared with your company
              </Box>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              This will create a copy of your report that all company members can view and edit.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {isAlreadyShared ? (
          <Button
            onClick={handleUnshare}
            color="error"
            variant="outlined"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Unshare'}
          </Button>
        ) : (
          <Button
            onClick={handleShare}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Share Report'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareReportDialog;
